import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Mail,
  ArrowLeft,
  Copy,
  Check,
  RefreshCw,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  Filter,
  AlertTriangle,
  ListChecks,
  RotateCw,
  X,
  Clock
} from 'lucide-react';
import { WorkflowCard } from '../shared/WorkflowCard';
import { isHighRiskOrWorse } from '../shared/clauseRisk';
import { WorkflowContext, EmailTone } from '../../../../types/workflow.types';
import { apiConfig } from '../../../../services/api/apiConfig';
import { decodeHtmlEntities } from '../../../../utils/textUtils';
import { ModelSelectionModal, SelectedModel } from '../shared/ModelSelectionModal';
import { contractService, EmailGenerationProgress, GenerationCheckpoint } from '../../../../services/api/contractService';

// LocalStorage key for client-side checkpoint
const LOCAL_CHECKPOINT_KEY = 'email_gen_checkpoint_';

interface LocalCheckpoint {
  contractId: string;
  sessionId: string;
  timestamp: number;
  elapsedTime: number;
  progress: EmailGenerationProgress | null;
  tone: string;
  clauseCount: number;
}

interface OutletContext extends WorkflowContext {
  onPrevStep: () => void;
  completeReview: (emailData?: { subject: string; body: string }) => Promise<void>;
  isSaving: boolean;
}

const toneOptions: { value: EmailTone; label: string; description: string }[] = [
  { value: 'formal', label: 'Formal', description: 'Professional and business-like tone' },
  { value: 'cooperative', label: 'Cooperative', description: 'Emphasizes partnership and mutual benefit' },
  { value: 'assertive', label: 'Assertive', description: 'Direct and firm about required changes' },
  { value: 'diplomatic', label: 'Diplomatic', description: 'Balanced approach with understanding' },
];

type ClauseFilterType = 'all' | 'flagged_rejected' | 'selected';

const clauseFilterOptions: { value: ClauseFilterType; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'All Clauses', description: 'Include all analyzed clauses', icon: <ListChecks className="w-5 h-5" /> },
  { value: 'flagged_rejected', label: 'Flagged / Rejected', description: 'Only clauses marked as flagged or rejected', icon: <AlertTriangle className="w-5 h-5" /> },
  { value: 'selected', label: 'Selected Only', description: 'Only clauses manually selected for negotiation', icon: <CheckCircle className="w-5 h-5" /> },
];

export const Step5_Negotiation: React.FC = () => {
  const context = useOutletContext<OutletContext>();
  const {
    contract,
    analysis,
    clauseDecisions,
    selectedForNegotiation,
    isLoading,
    isSaving: workflowSaving,
    onPrevStep,
    completeReview,
  } = context;

  const [isCompleting, setIsCompleting] = useState(false);

  const [selectedTone, setSelectedTone] = useState<EmailTone>('formal');
  const [clauseFilter, setClauseFilter] = useState<ClauseFilterType>('flagged_rejected');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<EmailGenerationProgress | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [copied, setCopied] = useState(false);
  const [showClauseDetails, setShowClauseDetails] = useState(false);
  const [showModelSelection, setShowModelSelection] = useState(false);

  // Recovery dialog state
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [serverCheckpoint, setServerCheckpoint] = useState<GenerationCheckpoint | null>(null);
  const [isResuming, setIsResuming] = useState(false);
  const [generationSessionId] = useState(() => crypto.randomUUID());

  // Get clauses based on the selected filter
  const filteredClauses = useMemo(() => {
    if (!analysis?.clauses) return [];

    let clauses = analysis.clauses;

    switch (clauseFilter) {
      case 'all':
        // Return all clauses
        break;
      case 'flagged_rejected':
        // Only clauses that are flagged or rejected
        clauses = clauses.filter(clause => {
          const decision = clauseDecisions[clause.reference];
          return decision?.status === 'flagged' || decision?.status === 'rejected';
        });
        break;
      case 'selected':
        // Only manually selected clauses
        clauses = clauses.filter(clause => selectedForNegotiation.includes(clause.reference));
        break;
    }

    return clauses.map(clause => ({
      ...clause,
      decision: clauseDecisions[clause.reference],
    }));
  }, [analysis?.clauses, clauseFilter, clauseDecisions, selectedForNegotiation]);

  // Alias for backward compatibility in generate email
  const selectedClauses = filteredClauses;

  // Count clauses per filter type for display
  const clauseCounts = useMemo(() => {
    if (!analysis?.clauses) return { all: 0, flagged_rejected: 0, selected: 0 };

    const flaggedRejected = analysis.clauses.filter(clause => {
      const decision = clauseDecisions[clause.reference];
      return decision?.status === 'flagged' || decision?.status === 'rejected';
    }).length;

    return {
      all: analysis.clauses.length,
      flagged_rejected: flaggedRejected,
      selected: selectedForNegotiation.length,
    };
  }, [analysis?.clauses, clauseDecisions, selectedForNegotiation]);

  // Helper function to format elapsed time
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer effect for elapsed time during generation
  useEffect(() => {
    if (isGenerating && generationStartTime) {
      // Start the timer interval
      timerIntervalRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - generationStartTime) / 1000));
      }, 1000);
    } else {
      // Clear the interval when not generating
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isGenerating, generationStartTime]);

  // Check for incomplete generation on mount
  useEffect(() => {
    const checkForCheckpoint = async () => {
      if (!contract?._id || emailBody) return;

      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        // Check server-side checkpoint first (more reliable)
        const checkpoint = await contractService.getGenerationCheckpoint(token, contract._id);

        if (checkpoint && checkpoint.status === 'in_progress') {
          // Check if checkpoint is recent (< 24 hours)
          const checkpointAge = Date.now() - new Date(checkpoint.lastUpdated).getTime();
          const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

          if (checkpointAge < MAX_AGE) {
            setServerCheckpoint(checkpoint);
            setShowRecoveryDialog(true);
          } else {
            // Clean up stale checkpoint
            await contractService.clearGenerationCheckpoint(token, contract._id);
          }
        }
      } catch (error) {
        console.error('Error checking for checkpoint:', error);
      }
    };

    checkForCheckpoint();
  }, [contract?._id, emailBody]);

  // Save local checkpoint during generation
  const saveLocalCheckpoint = useCallback(() => {
    if (!contract?._id) return;

    const checkpoint: LocalCheckpoint = {
      contractId: contract._id,
      sessionId: generationSessionId,
      timestamp: Date.now(),
      elapsedTime,
      progress: generationProgress,
      tone: selectedTone,
      clauseCount: selectedClauses.length
    };

    localStorage.setItem(LOCAL_CHECKPOINT_KEY + contract._id, JSON.stringify(checkpoint));
  }, [contract?._id, generationSessionId, elapsedTime, generationProgress, selectedTone, selectedClauses.length]);

  // Save checkpoint on progress update
  useEffect(() => {
    if (isGenerating && generationProgress) {
      saveLocalCheckpoint();
    }
  }, [isGenerating, generationProgress, saveLocalCheckpoint]);

  // Clear local checkpoint on completion
  const clearLocalCheckpoint = useCallback(() => {
    if (!contract?._id) return;
    localStorage.removeItem(LOCAL_CHECKPOINT_KEY + contract._id);
  }, [contract?._id]);

  // Handle resume from checkpoint
  const handleResume = async () => {
    if (!contract?._id || !serverCheckpoint) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found');
      return;
    }

    setShowRecoveryDialog(false);
    setIsResuming(true);
    setIsGenerating(true);
    setGenerationStartTime(Date.now());
    setElapsedTime(0);

    try {
      const generatedEmail = await contractService.resumeEmailGeneration(
        token,
        contract._id,
        (progress) => {
          setGenerationProgress(progress);
        }
      );

      setEmailSubject(`Contract Review - ${decodeHtmlEntities(contract.contractTitle)} - Discussion Points`);
      setEmailBody(generatedEmail);
      setGenerationProgress(null);
      clearLocalCheckpoint();
    } catch (error) {
      console.error('Error resuming email generation:', error);
      // On error, offer to start fresh
      alert('Failed to resume generation. Please try generating again.');
    } finally {
      setIsGenerating(false);
      setIsResuming(false);
      setGenerationProgress(null);
      setGenerationStartTime(null);
    }
  };

  // Handle start fresh (discard checkpoint)
  const handleStartFresh = async () => {
    if (!contract?._id) return;

    const token = localStorage.getItem('authToken');
    if (token) {
      await contractService.clearGenerationCheckpoint(token, contract._id);
    }
    clearLocalCheckpoint();
    setShowRecoveryDialog(false);
    setServerCheckpoint(null);
  };

  // Handle model selection and generate email
  const handleModelSelect = useCallback(async (selection: SelectedModel) => {
    setShowModelSelection(false);
    await generateEmailWithModel(selection.modelId, selection.provider);
  }, []);

  // Generate email using backend AI API with senior lawyer expertise
  const generateEmailWithModel = async (model?: string, provider?: 'anthropic' | 'openai') => {
    if (!contract || selectedClauses.length === 0) return;

    setIsGenerating(true);
    setGenerationProgress(null);
    setGenerationStartTime(Date.now());
    setElapsedTime(0);

    try {
      // Prepare clauses for the API call
      const clausesForApi = selectedClauses.map(clause => ({
        clauseNumber: clause.reference,
        clauseTitle: clause.title,
        priority: clause.negotiation_analysis?.classification === 'deal-breaker' ? 'deal-breaker' :
                  clause.risk_assessment?.legal_risk === 'High' ? 'high' : 'medium',
        concern: clause.problematic_subclauses?.[0]?.issue || clause.party_obligations || 'Requires review',
        suggestedRevision: clause.negotiation_analysis?.recommended_modification || '',
        comments: clause.decision?.comment ? [clause.decision.comment] : []
      }));

      // Call the backend API - token is stored as 'authToken' by AuthContext
      const token = localStorage.getItem('authToken');

      if (!token) {
        console.error('No auth token found in localStorage');
        throw new Error('Please log in again to generate negotiation emails');
      }

      // Use streaming endpoint for 10+ clauses to handle large email generation
      const useStreaming = clausesForApi.length >= 10;

      if (useStreaming) {
        // Use the streaming service for large clause counts
        const emailData = {
          contractName: decodeHtmlEntities(contract.contractTitle),
          supplierName: typeof contract.supplier === 'object'
            ? (contract.supplier as any)?.companyName || 'Supplier'
            : contract.supplier || 'Supplier',
          clauses: clausesForApi as Array<{
            clauseNumber: string;
            clauseTitle: string;
            concern: string;
            suggestedRevision?: string;
            priority: 'deal-breaker' | 'high' | 'medium' | 'low';
            comments?: string[];
          }>,
          tone: selectedTone
        };

        const generatedEmail = await contractService.generateNegotiationEmailWithProgress(
          token,
          emailData,
          (progress) => {
            setGenerationProgress(progress);
          }
        );

        setEmailSubject(`Contract Review - ${decodeHtmlEntities(contract.contractTitle)} - Discussion Points`);
        setEmailBody(generatedEmail);
        setGenerationProgress(null);
        return;
      }

      // Standard non-streaming approach for smaller clause counts
      const response = await fetch(`${apiConfig.baseURL}${apiConfig.endpoints.contracts.generateNegotiationEmail}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contractName: decodeHtmlEntities(contract.contractTitle),
          // Handle both string and populated supplier object
          supplierName: typeof contract.supplier === 'object'
            ? (contract.supplier as any)?.companyName || 'Supplier'
            : contract.supplier || 'Supplier',
          clauses: clausesForApi,
          tone: selectedTone,
          model,
          provider
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error response:', errorData, 'Status:', response.status);
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log out and log in again.');
        }
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }
        throw new Error(errorData.message || `Failed to generate email: ${response.status}`);
      }

      const data = await response.json();
      console.log('API response:', data);

      if (data.success && data.email) {
        setEmailSubject(`Contract Review - ${decodeHtmlEntities(contract.contractTitle)} - Discussion Points`);
        setEmailBody(data.email);
        return; // Exit early on success
      } else {
        throw new Error(data.message || 'Failed to generate email');
      }
    } catch (error) {
      console.error('Error generating email:', error);
      console.log('API URL used:', apiConfig.baseURL);
      // Fallback to local generation with senior lawyer recommendations if API fails
      const toneText = {
        formal: 'I am writing to discuss',
        cooperative: 'I hope this message finds you well. As we continue our valued partnership, I wanted to discuss',
        assertive: 'Following our review of the contract, we must address',
        diplomatic: 'Thank you for the opportunity to work together. After careful review, we would like to respectfully discuss',
      };

      // Generate senior lawyer-level recommendations based on clause type
      const generateResolution = (clause: typeof selectedClauses[0]): string => {
        const title = clause.title.toLowerCase();
        const content = ((clause.party_obligations || '') + ' ' + (clause.summary || '')).toLowerCase();
        const concern = (clause.problematic_subclauses?.[0]?.issue || '').toLowerCase();
        const reference = (clause.reference || '').toLowerCase();

        // FORCE MAJEURE - Check FIRST (specific clause type)
        if (title.includes('force majeure') || content.includes('force majeure')) {
          return `Revise to: (a) extend trigger period to 90 days minimum; (b) require mutual termination right; (c) include pandemic/epidemic explicitly; (d) suspend payment obligations during FM event; (e) require mitigation efforts and regular status updates.`;
        }

        // CHANGE OF CONTROL / ASSIGNMENT / MERGER - Check early (specific clause type)
        if (title.includes('change of control') || title.includes('merger') || concern.includes('consent mechanism') ||
            content.includes('change of control') || content.includes('merger or consolidation')) {
          return `Request: (a) assignment to affiliates without consent; (b) assignment in M&A transactions with 90-day notice; (c) mutual restrictions on assignment to competitors; (d) change of control triggers discussion period, not automatic termination.`;
        }

        // SANCTIONS - Check early (specific clause type)
        if (title.includes('sanction') || content.includes('sanction') || concern.includes('reason to believe') ||
            concern.includes('suspicion') || content.includes('sanctioned')) {
          return `Request: (a) replace "reason to believe" with "actual knowledge"; (b) require written notice and 30-day cure period; (c) limit to direct violations, not mere association; (d) mutual representations regarding sanctions compliance.`;
        }

        // GREY MARKET / EXTRA-TERRITORIAL - Check before termination
        if (title.includes('grey market') || content.includes('grey market') || title.includes('extra-territorial') ||
            concern.includes('grey market') || concern.includes('strict liability') || concern.includes('inadvertent') ||
            (title.includes('territorial') && concern.includes('fault'))) {
          return `Add knowledge/intent requirement before liability attaches. Require: (a) written notice of alleged violation; (b) 30-day investigation period; (c) opportunity to cure by terminating offending sub-distributor; (d) exclusion for sales through authorized channels that are later diverted by third parties.`;
        }

        // EXCHANGE RATE / CURRENCY - Check before termination
        if (title.includes('exchange') || title.includes('currency') || content.includes('exchange rate') ||
            concern.includes('barclays') || concern.includes('conversion rate') || concern.includes('hidden profit')) {
          return `Request: (a) use of mid-market rate from Reuters/Bloomberg; (b) option to pay in either currency; (c) 2% tolerance band before adjustments apply; (d) advance notice of rate to be applied; (e) right to dispute within 30 days.`;
        }

        // TERMINATION - Check after specific clause types
        if (title.includes('termination') ||
            (reference.includes('16.2') && !title.includes('change') && !title.includes('sanction') && !title.includes('grey')) ||
            (reference.includes('16.3') && !title.includes('change') && !title.includes('sanction') && !title.includes('grey') && !title.includes('extra')) ||
            concern.includes('cure period') ||
            (content.includes('terminate') && !content.includes('force majeure'))) {
          return `Extend cure period to 60 days for curable breaches with written notice specifying breach details. Add: (a) wind-down period for existing orders; (b) inventory repurchase at 100% of cost; (c) 6-month transition assistance; (d) protection of accrued commissions; (e) exclude minor/technical breaches from termination triggers.`;
        }

        // GOODWILL / COMPENSATION - Check early
        if (title.includes('goodwill') || title.includes('compensation') ||
            concern.includes('goodwill') || concern.includes('distributor protection')) {
          return `Request: (a) compensation for goodwill upon termination without cause equal to 12 months average margin; (b) mandatory compliance with local distributor protection laws; (c) buy-back of inventory and marketing materials at cost.`;
        }

        // FINANCIAL DETERIORATION - specific check
        if (title.includes('financial deterioration') || concern.includes('financial') && concern.includes('subjective')) {
          return `Request: (a) objective financial metrics (e.g., credit rating, debt-to-equity ratio) instead of subjective assessment; (b) 90-day notice and opportunity to provide additional security; (c) right to cure by demonstrating financial stability; (d) mutual application to both parties.`;
        }

        // INTEREST RATE - Check before general financial
        if (title.includes('interest rate') || title.includes('interest') ||
            (concern.includes('interest') && concern.includes('market standard'))) {
          return `Request: (a) reduce interest to base rate + 2% (market standard); (b) interest only on undisputed amounts; (c) 30-day grace period before interest accrues; (d) mutual application to late payments by either party.`;
        }

        // EXCHANGE RATE / CURRENCY
        if (title.includes('exchange') || title.includes('currency') || content.includes('exchange rate') ||
            concern.includes('barclays') || concern.includes('conversion')) {
          return `Request: (a) use of mid-market rate from Reuters/Bloomberg; (b) option to pay in either currency; (c) 2% tolerance band before adjustments apply; (d) advance notice of rate to be applied; (e) right to dispute within 30 days.`;
        }

        // BINDING CALCULATIONS / MANIFEST ERROR
        if (title.includes('binding') || title.includes('calculation') || concern.includes('manifest error')) {
          return `Request: (a) lower standard of "clear error" instead of "manifest error"; (b) 30-day dispute period for all calculations; (c) independent third-party determination of disputed amounts; (d) interest suspension during disputes.`;
        }

        // PRICING / PAYMENT / RESALE PRICE
        if (title.includes('payment') || title.includes('price') || title.includes('pricing') ||
            title.includes('resale') || concern.includes('margin') || concern.includes('price increase')) {
          return `Request: (a) minimum 25% discount from list price; (b) Net 45-60 days payment terms; (c) 2% early payment discount; (d) annual price increases capped at CPI + 2%; (e) 6-month price freeze for new products; (f) most favored customer pricing clause.`;
        }

        // MINIMUM / PERFORMANCE / ADVERTISING SPEND
        if (title.includes('minimum') || title.includes('advertising') || title.includes('spend') ||
            concern.includes('undefined minimum') || concern.includes('unlimited') && concern.includes('exposure')) {
          return `Request: (a) clearly defined minimum amounts specified in contract; (b) annual adjustment mechanism tied to market conditions; (c) 90-day cure period before consequences; (d) reduction for events beyond Distributor's control; (e) carry-forward credit for over-performance.`;
        }

        // WARRANTY / CRITERIA - but not distribution criteria with unilateral rights
        if (concern.includes('unilateral') || concern.includes('without consent') || concern.includes('impossible')) {
          return `Request: (a) mutual consent required for any modifications; (b) 90-day advance notice of proposed changes; (c) changes limited to reasonable industry standards; (d) right to terminate if changes materially adverse; (e) grandfather clause for existing practices.`;
        }

        // WARRANTY (general)
        if (title.includes('warranty') || content.includes('represents and warrants')) {
          return `Limit warranties: (a) knowledge-qualified representations ("to Distributor's knowledge"); (b) 30-day grace period to cure non-compliance; (c) materiality threshold before breach occurs; (d) mutual representations from Supplier regarding product quality and IP rights.`;
        }

        // COMPLIANCE BREACH - specific
        if (title.includes('compliance') || concern.includes('materiality threshold') || concern.includes('minor') && concern.includes('breach')) {
          return `Request: (a) materiality threshold - only "material" breaches trigger consequences; (b) 30-day written notice and cure period; (c) exclusion of technical/administrative breaches; (d) escalation procedure before termination.`;
        }

        // EXCLUSIVITY / NON-EXCLUSIVE APPOINTMENT - Check last among distribution-related
        if ((title.includes('exclusive') && title.includes('appointment')) ||
            (content.includes('non-exclusive') && content.includes('appoint'))) {
          return `Request conversion to exclusive distribution rights for 24-36 months, subject to minimum purchase commitments. Alternatively, request "most favored distributor" status with: (a) right of first refusal on new appointments; (b) maximum of 2 additional distributors in Territory; (c) 90-day prior notice of new appointments with opportunity to match terms.`;
        }

        // LIABILITY / INDEMNIFICATION
        if (title.includes('liability') || title.includes('indemnif') || title.includes('limitation')) {
          return `Cap aggregate liability at 12 months of fees paid. Carve out only: (i) gross negligence/willful misconduct; (ii) IP infringement; (iii) death/personal injury. Add mutual indemnification with standard defense procedures.`;
        }

        // AUDIT / RECORDS
        if (title.includes('audit') || title.includes('inspection') || title.includes('records')) {
          return `Limit audit rights: (a) once annually with 30 days notice; (b) during business hours only; (c) by independent auditor under confidentiality; (d) at auditing party's expense unless discrepancy exceeds 5%.`;
        }

        // DEFAULT - Check if negotiation_analysis has something useful (but not generic)
        const modification = clause.negotiation_analysis?.recommended_modification || '';
        if (modification && modification.length > 30 && !modification.toLowerCase().includes('modify as follows')) {
          return modification;
        }

        // True fallback with specific actionable language
        return `Request revision to ensure: (a) mutual and balanced obligations applying equally to both parties; (b) replacement of subjective standards with objective, measurable criteria; (c) reasonable notice and cure periods of at least 30 days; (d) caps on financial exposure with clear limitations; (e) good faith consultation before exercise of discretionary rights.`;
      };

      const subject = `Contract Review - ${decodeHtmlEntities(contract.contractTitle)} - Discussion Points`;

      const clausesList = selectedClauses.map((clause, index) => {
        const resolution = generateResolution(clause);
        const comment = clause.decision?.comment ? `\n   Internal Note: ${clause.decision.comment}` : '';
        return `${index + 1}. **${clause.reference} - ${clause.title}**
   Current Position: ${clause.party_obligations || 'As stated in the contract'}
   Our Concern: ${clause.problematic_subclauses?.[0]?.issue || 'Requires clarification'}
   Proposed Resolution: ${resolution}${comment}
`;
      }).join('\n');

      // Get supplier name for fallback email
      const supplierName = typeof contract.supplier === 'object'
        ? (contract.supplier as any)?.companyName || 'Supplier'
        : contract.supplier || 'Supplier';

      const body = `Dear ${supplierName} Team,

${toneText[selectedTone]} the following points in our contract agreement "${decodeHtmlEntities(contract.contractTitle)}":

${clausesList}

**Executive Summary of Key Requests:**
${selectedClauses.slice(0, 5).map((c, i) => `${i + 1}. ${c.title}: Require balanced terms with mutual protections`).join('\n')}

We believe these adjustments would create a more balanced agreement that serves both parties' interests effectively. We are open to discussing alternative approaches that address our concerns while meeting your business requirements.

Please let us know your availability for a call to discuss these points at your earliest convenience.

Best regards,
[Your Name]
[Your Title]
[Your Company]`;

      setEmailSubject(subject);
      setEmailBody(body);
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
      setGenerationStartTime(null);
      // Clear local checkpoint on completion (success or fallback)
      clearLocalCheckpoint();
    }
  };

  const copyToClipboard = async () => {
    const fullEmail = `Subject: ${emailSubject}\n\n${emailBody}`;
    await navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const regenerateEmail = () => {
    setEmailSubject('');
    setEmailBody('');
    setShowModelSelection(true);
  };

  // Show model selection modal when user clicks generate
  const handleGenerateClick = () => {
    if (!contract || selectedClauses.length === 0) return;
    setShowModelSelection(true);
  };

  if (isLoading || !contract) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Recovery Dialog Modal */}
      {showRecoveryDialog && serverCheckpoint && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Resume Email Generation?
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Incomplete generation found
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRecoveryDialog(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Progress:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {serverCheckpoint.completedBatches} / {serverCheckpoint.totalBatches} batches
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${(serverCheckpoint.completedBatches / serverCheckpoint.totalBatches) * 100}%`
                  }}
                />
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Clauses:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {serverCheckpoint.inputData.clauseCount} clauses
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Started:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {new Date(serverCheckpoint.initiatedAt).toLocaleString()}
                </span>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              You can resume from where you left off or start fresh with a new generation.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleStartFresh}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={handleResume}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <RotateCw className="w-4 h-4" />
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Negotiation Email
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Generate and customize your negotiation email
          </p>
        </div>
      </div>

      {/* Selected Items Summary */}
      <WorkflowCard
        title={`Selected Items (${selectedClauses.length})`}
        actions={
          <button
            onClick={() => setShowClauseDetails(!showClauseDetails)}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            {showClauseDetails ? 'Hide Details' : 'Show Details'}
            {showClauseDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        }
      >
        {selectedClauses.length > 0 ? (
          <div className="space-y-2">
            {selectedClauses.map((clause, clauseIndex) => (
              <div
                key={clause.id || `${clause.reference}-${clauseIndex}`}
                className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {clause.reference} - {clause.title}
                    </span>
                    {showClauseDetails && (
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        {clause.problematic_subclauses?.[0]?.issue && (
                          <p><strong>Issue:</strong> {clause.problematic_subclauses[0].issue}</p>
                        )}
                        {clause.negotiation_analysis?.recommended_modification && (
                          <p><strong>Recommendation:</strong> {clause.negotiation_analysis.recommended_modification}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {isHighRiskOrWorse(clause) && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded">
                      High Risk
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No items selected for negotiation. Go back to Summary to select items.
          </div>
        )}
      </WorkflowCard>

      {/* Clause Selection Filter */}
      <WorkflowCard
        title="Select Clauses to Include"
        icon={<Filter className="w-5 h-5" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {clauseFilterOptions.map(option => (
            <button
              key={option.value}
              onClick={() => {
                setClauseFilter(option.value);
                // Clear generated email when filter changes
                if (emailBody) {
                  setEmailSubject('');
                  setEmailBody('');
                }
              }}
              className={`p-4 rounded-lg border transition-all text-left ${
                clauseFilter === option.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500/20'
                  : 'border-gray-200 dark:border-dark-border hover:border-primary-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  clauseFilter === option.value
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                }`}>
                  {option.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className={`font-medium ${
                      clauseFilter === option.value
                        ? 'text-primary-700 dark:text-primary-400'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {option.label}
                    </p>
                    <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${
                      clauseFilter === option.value
                        ? 'bg-primary-200 dark:bg-primary-800 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {clauseCounts[option.value]}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {option.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </WorkflowCard>

      {/* Tone Selection */}
      <WorkflowCard title="Email Tone">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {toneOptions.map(tone => (
            <button
              key={tone.value}
              onClick={() => setSelectedTone(tone.value)}
              className={`p-3 rounded-lg border transition-all text-left ${
                selectedTone === tone.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-dark-border hover:border-primary-300'
              }`}
            >
              <p className={`font-medium ${
                selectedTone === tone.value
                  ? 'text-primary-700 dark:text-primary-400'
                  : 'text-gray-900 dark:text-white'
              }`}>
                {tone.label}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {tone.description}
              </p>
            </button>
          ))}
        </div>
      </WorkflowCard>

      {/* Generate Button or Email Editor */}
      {!emailBody ? (
        <WorkflowCard>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Ready to Generate
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
              Click the button below to generate a negotiation email based on your selected items and chosen tone.
            </p>

            {/* Clause count info and warning for large counts */}
            <div className="mb-6 max-w-md mx-auto">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedClauses.length}</span> clauses will be analyzed
              </p>
              {selectedClauses.length >= 100 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-left">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Large clause count detected
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                      Processing {selectedClauses.length} clauses may take 5-10 minutes. The email will be generated in multiple parts for comprehensive coverage.
                    </p>
                  </div>
                </div>
              )}
              {selectedClauses.length >= 50 && selectedClauses.length < 100 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Estimated time: 2-5 minutes
                </p>
              )}
            </div>

            <button
              onClick={handleGenerateClick}
              disabled={isGenerating || selectedClauses.length === 0}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Email
                </>
              )}
            </button>

            {/* Progress UI for generation with elapsed time */}
            {isGenerating && (
              <div className="mt-6 w-full max-w-md mx-auto space-y-3">
                {/* Elapsed time display */}
                <div className="text-center">
                  <span className="text-2xl font-mono font-bold text-primary-600 dark:text-primary-400">
                    {formatElapsedTime(elapsedTime)}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Elapsed time
                  </p>
                </div>

                {generationProgress ? (
                  <>
                    <div className="flex items-center justify-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-primary-600 dark:text-primary-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {generationProgress.message}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${generationProgress.percentage}%` }}
                      />
                    </div>
                    {generationProgress.currentBatch && generationProgress.totalBatches && (
                      <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                        Processing batch {generationProgress.currentBatch} of {generationProgress.totalBatches}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-primary-600 dark:text-primary-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Generating email...
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Model Selection Modal */}
          <ModelSelectionModal
            isOpen={showModelSelection}
            onClose={() => setShowModelSelection(false)}
            onSelectModel={handleModelSelect}
          />
        </WorkflowCard>
      ) : (
        <WorkflowCard
          title="Generated Email"
          actions={
            <div className="flex gap-2">
              <button
                onClick={regenerateEmail}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <RefreshCw className="w-4 h-4" />
                Regenerate
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {/* Subject Line */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Email Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Body
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm resize-y"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
              <button
                onClick={copyToClipboard}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy to Clipboard
                  </>
                )}
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                Open in Email Client
              </button>
            </div>
          </div>
        </WorkflowCard>
      )}

      {/* Success Message */}
      {emailBody && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-300">
                Negotiation email generated successfully
              </h4>
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                Review the email above, make any necessary edits, and copy it to send to your counterparty.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onPrevStep}
          disabled={isCompleting}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Summary
        </button>
        <button
          onClick={async () => {
            setIsCompleting(true);
            try {
              // Pass the generated email data if available
              const emailDataToSave = emailBody ? { subject: emailSubject, body: emailBody } : undefined;
              await completeReview(emailDataToSave);
              // Use setTimeout to ensure the save completes before navigation
              setTimeout(() => {
                window.location.href = '/';
              }, 500);
            } catch (error) {
              console.error('Error completing review:', error);
              setIsCompleting(false);
            }
          }}
          disabled={isCompleting || workflowSaving}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          {isCompleting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving Review...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Complete Review
            </>
          )}
        </button>
      </div>
    </div>
  );
};
